import { FormEvent, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoginIcon from "@mui/icons-material/Login";
import {
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";

// Internal imports
import { MGButton, MGInputBox } from "../../../components";
import carnival from "../../../resources/images/carnival.jpg";
import loginSideImage from "../../../resources/images/login-side-image.jpg";
import { useDocumentTitle, useAuthService } from "../../../hooks";
import { ILoginFormErrors } from "./Login.types";
import { EMAIL_REGEX } from "../../../shared/constants";
import { API } from "../../../constants";
import { ActionTypes, GlobalContextData } from "../../../context";

// style
import "./Login.scss";
import { RoutePath } from "../../../routes";
import { Switch } from "@mui/material";

let abortController: any = null;

export const Login = () => {
  const { isLoading: isSubmitting, auth } = useAuthService(API.AUTH.LOGIN);
  const { dispatch } = useContext(GlobalContextData);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<ILoginFormErrors>({});
  const [passKeyAuthSupported, setPassKeyAuthSupported] = useState(true);
  const [credentials, setCredentials] = useState<PublicKeyCredential[]>([]);
  const [loginWithPassKey, setLoginWithPassKey] = useState(true);

  const navigate = useNavigate();

  useDocumentTitle("Login");

  useEffect(() => {
    const isSupported = browserSupportsWebAuthn();
    setPassKeyAuthSupported(isSupported);
    if (!isSupported) {
      setLoginWithPassKey(true);
    }
    sessionStorage.removeItem("token");
    // const checkPasskeys = async () => {
    //   if (browserSupportsWebAuthn()) {
    //     setPassKeyAuthSupported(true);
    //     try {
    //       const credentials = await navigator.credentials.get({
    //         publicKey: {
    //           challenge: new Uint8Array(32),
    //           allowCredentials: [],
    //           timeout: 60000,
    //         },
    //       });
    //       if (credentials) {
    //         setCredentials([credentials as PublicKeyCredential]);
    //       }
    //     } catch (error) {
    //       setCredentials([]);
    //     }
    //   }
    // };

    // checkPasskeys();
    // handlePasskeyAuth();
    dispatch({
      type: ActionTypes.RESET_STATE,
      payload: {},
    });
  }, []);

  const validateForm = () => {
    let isValid = true;
    const newErrors: ILoginFormErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!EMAIL_REGEX.test(email)) {
      newErrors.email = "Email is invalid";
      isValid = false;
      toast.error("Email is invalid");
    }

    if (!password) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    handleAbort();

    const payload = { email, password };

    try {
      const response = await auth(payload);
      const { data } = response;

      const { message, AuthenticationResult, session, newPasswordRequired } =
        data;

      console.log("data", data);
      if (message === "Invalid credentials.") {
        toast.error(message);
        return;
      }

      if (session && newPasswordRequired) {
        navigate("/reset-password", { state: { email, session } });
        return;
      }

      if (AuthenticationResult.IdToken) {
        sessionStorage.setItem("token", AuthenticationResult.IdToken);

        navigate("/dashboard", { replace: true });
      } else {
        toast.error("Something went wrong!!");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || "Something went wrong!!";
      toast.error(errorMessage);
    }
  };

  const handlePasskeyAuth = async () => {
    if (!browserSupportsWebAuthn()) {
      toast.error("This browser doesn't support WebAuthn.");
      return;
    }

    try {
      const baseUrl = `${process.env.REACT_APP_AUTH_SERVER as string}/auth`;
      const resp = await fetch(baseUrl + "/generate-authentication-options", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId: email }),
        credentials: "include",
      });
      const opts = await resp.json();
      if (opts.error === "User not found") {
        alert("User has not enabled passkey authentication");
        return;
      }
      const asseResp = await startAuthentication(opts);
      console.log("ATTResP:", asseResp);

      const verificationResp = await fetch(baseUrl + "/verify-authentication", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...asseResp,
          userId: email,
        }),
      });

      const verificationJSON = await verificationResp.json();

      if (
        verificationJSON &&
        verificationJSON.verified &&
        verificationJSON.token
      ) {
        console.log("Token Passkey: ", verificationJSON);
        sessionStorage.setItem("token", verificationJSON.token);
        navigate("/dashboard", { replace: true });
      } else {
        toast.error("Authentication failed.");
      }
    } catch (error: any) {
      console.log("error:", error);
      toast.error("Authentication error: " + error?.message);
    }
  };

  const handlePasskeyAuth1 = async (credential: PublicKeyCredential) => {
    if (!browserSupportsWebAuthn()) {
      toast.error("This browser doesn't support WebAuthn.");
      return;
    }
    try {
      const baseUrl = `${process.env.REACT_APP_AUTH_SERVER as string}/auth`;
      const resp = await fetch(baseUrl + "/generate-authentication-options", {
        credentials: "include",
      });
      const opts = await resp.json();
      opts.allowCredentials = [{ id: credential.rawId, type: "public-key" }];
      const asseResp = await startAuthentication(opts);

      const verificationResp = await fetch(baseUrl + "/verify-authentication", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(asseResp),
      });

      const verificationJSON = await verificationResp.json();

      if (verificationJSON && verificationJSON.verified) {
        console.log("Token Passkey: ", verificationJSON);
        // sessionStorage.setItem("token", verificationJSON.token);
        // navigate("/dashboard", { replace: true });
        alert("Success");
      } else {
        toast.error("Authentication failed.");
      }
    } catch (error: any) {
      toast.error("Authentication error: " + error.message);
    }
  };

  function handleAbort() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  return (
    <section className="login-container">
      <div className="login-form-section">
        <div className="logo-container">
          <img src={carnival} alt="Logo" className="logo-image" />
        </div>
        <div className="login-title">
          <span className="login-title-first">Crew Transportation</span>
          <span className="login-title-sec"> System</span>
        </div>
        <p className="login-desc">Please sign in to continue</p>
        <form className="form-section">
          <MGInputBox
            label="Email"
            required
            name="email"
            type="text"
            placeholder="Email..."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            autoComplete={
              passKeyAuthSupported ? "username webauthn" : "username"
            }
          />

          {(loginWithPassKey || !passKeyAuthSupported) && (
            <MGInputBox
              label="Password"
              required
              name="password"
              placeholder="Password..."
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
            />
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            {passKeyAuthSupported && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Switch
                  {...{
                    inputProps: { "aria-label": "Login with Passkey" },
                  }}
                  checked={loginWithPassKey}
                  defaultChecked
                  onChange={() => setLoginWithPassKey(!loginWithPassKey)}
                />

                <p className="forgot-password">Login with Password</p>
              </div>
            )}
            <p className="forgot-password" style={{ marginLeft: "auto" }}>
              <Link className="link-css" to={RoutePath.FORGOT_PASSWORD}>
                Forgot password ?
              </Link>
            </p>
          </div>
          {loginWithPassKey && passKeyAuthSupported && (
            <MGButton
              size="large"
              disabled={isSubmitting}
              type="submit"
              tabIndex={0}
              className="submit-button"
              onClick={handleSubmit}
            >
              {isSubmitting ? "Logging you in..." : "Login"}
              <LoginIcon className="pad-left-5" />
            </MGButton>
          )}

          {passKeyAuthSupported ? (
            <div style={{ marginTop: "1rem", gap: "1rem" }}>
              {!loginWithPassKey && (
                <MGButton
                  size="large"
                  type="button"
                  tabIndex={0}
                  disabled={!email}
                  className="submit-button"
                  style={{ marginBottom: "1rem" }}
                  onClick={handlePasskeyAuth}
                >
                  Login with Passkey
                </MGButton>
              )}
              {passKeyAuthSupported && credentials.length > 0 && (
                <div className="passkey-credentials">
                  <h3>Login with Passkey</h3>
                  {credentials.map((credential, index) => (
                    <MGButton
                      key={index}
                      onClick={() => handlePasskeyAuth1(credential)}
                    >
                      Use Passkey {index + 1}
                    </MGButton>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <MGButton
                size="large"
                disabled={isSubmitting}
                type="submit"
                tabIndex={0}
                className="submit-button"
                onClick={handleSubmit}
              >
                {isSubmitting ? "Logging you in..." : "Login"}
                <LoginIcon className="pad-left-5" />
              </MGButton>
            </div>
          )}

          <p className="not-member">
            Not a member?{" "}
            <Link className="link-css" to="/vendor-registration">
              Register now
            </Link>
          </p>
        </form>
      </div>
      <div className="image-form-section">
        <img
          src={loginSideImage}
          alt="Logo"
          className="image"
          loading="eager"
        />
      </div>
    </section>
  );
};
